/**
 * MobileBottomSheet — bottom-anchored sheet on mobile, optional
 * centred presentation on ≥sm. Implements Contract §3.
 *
 *   • fixed bottom-0 inset-x-0 on mobile (or centred if requested)
 *   • drag handle pill (cosmetic — drag-to-dismiss not in v1)
 *   • safe-area-inset-bottom mandatory on footer
 *   • max-h-screen-safe + internal scroll
 *   • z-[1100] so it sits over Active Operation pill
 *
 * Two presentation modes:
 *   ── Structured (default) ──
 *     Pass `title`, `description`, `footer`. The primitive renders the
 *     dark-theme sticky header/footer + scrolling body shell.
 *
 *   ── Bare (bareContent=true) ──
 *     Caller owns the entire panel JSX (colours, padding, rounded
 *     corners, anchor position). The primitive only enforces the
 *     wrapper invariants: backdrop, ESC, scroll lock, focus
 *     restoration, safe-area bottom, and overscroll-contain. Use
 *     this when migrating hand-rolled sheets that already have a
 *     bespoke visual identity (e.g. AdminRoleSwitcher, the long-
 *     press ActiveOperationInfoSheet).
 *
 * API mirrors MobileDialog so Phase 2 migration is mechanical.
 *
 * Phase 1 — Mobile Shell programme. May 14, 2026.
 */
import { X } from "lucide-react";
import { MobileOverlayBackdrop } from "./MobileOverlayBackdrop";export function MobileBottomSheet({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  testid = "mobile-bottom-sheet",
  widthClass = "sm:max-w-md",
  priority = "sheet",   // Contract §9 — sheet defaults to z-[1100]
  dismissible = true,
  showHandle = true,
  className = "",
  bareContent = false,
}) {
  // Bare mode: the caller's children ARE the panel. We still wrap in
  // the backdrop primitive so the overlay invariants hold (ESC,
  // scroll-lock, focus restoration, backdrop dismiss), but we don't
  // impose any visual styling on the inner panel.
  if (bareContent) {
    return (
      <MobileOverlayBackdrop
        open={open}
        onClose={onClose}
        priority={priority}
        dismissible={dismissible}
        testid={`${testid}-backdrop`}
      >
        {children}
      </MobileOverlayBackdrop>
    );
  }

  return (
    <MobileOverlayBackdrop
      open={open}
      onClose={onClose}
      priority={priority}
      dismissible={dismissible}
      testid={`${testid}-backdrop`}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? `${testid}-title` : undefined}
        data-testid={testid}
        onClick={(e) => e.stopPropagation()}
        className={[
          "w-full", widthClass,
          "max-h-screen-safe overflow-hidden flex flex-col",
          "rounded-t-3xl sm:rounded-2xl",
          "bg-[#0a0a0a] border border-white/10 text-white shadow-2xl",
          className,
        ].filter(Boolean).join(" ")}
      >
        {showHandle && (
          <div className="pt-2.5 pb-1.5 flex justify-center" aria-hidden="true">
            <div className="w-10 h-1.5 rounded-full bg-white/15" />
          </div>
        )}

        {(title || dismissible) && (
          <div className="flex items-start gap-3 px-5 pt-2 pb-3 border-b border-white/[0.06]">
            <div className="flex-1 min-w-0">
              {title && (
                <h2 id={`${testid}-title`} className="text-base font-bold text-white truncate">
                  {title}
                </h2>
              )}
              {description && (
                <p className="text-[12.5px] text-zinc-400 mt-0.5">{description}</p>
              )}
            </div>
            {dismissible && (
              <button
                type="button"
                data-testid={`${testid}-close`}
                aria-label="Close"
                onClick={onClose}
                className="flex-shrink-0 w-9 h-9 -mr-2 -mt-1 rounded-full hover:bg-white/[0.06] flex items-center justify-center text-zinc-300 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        <div
          data-testid={`${testid}-body`}
          className="flex-1 overflow-y-auto overscroll-contain px-5 py-4"
        >
          {children}
        </div>

        {footer && (
          <div
            data-testid={`${testid}-footer`}
            className="border-t border-white/[0.06] px-5 pt-3 pb-safe-4 flex flex-wrap gap-2 justify-end"
          >
            {footer}
          </div>
        )}
      </div>
    </MobileOverlayBackdrop>
  );
}

export default MobileBottomSheet;
